import { Widget } from "@foretagsplatsen/widgetjs";

/**
 *`DoughnutChartWidget` displays an array of items containing a value and a color as a DoughnutChart.
 * One of the items can be highlighted and be shown with some specific data inside the doughnut.
 *
 * @param {{}[]} items - An array of objects containing magnitudes, colors, and values (any object) for each segment of the chart
 * that will be displayed (Ex: items =  [{magnitude: 4, color: 'blue', value: {}}, {magnitude: 2, color: 'red', value: {}] ).
 * @param {int} [radius] - An integer representing the total radius of the doughnut. A value between 100 and 150 is recommended.
 * @param {int} [doughnutWidth] - An integer representing the width of the chart segments. A value between 30 and 60 is recommmended.
 * @param {Renderable} [title] - Information displayed using a large font at the center of the doughnut when there
 * is no highlighted item. This can for example be used to show the purpose of the chart, e.g., "Total receivables".
 * @param {Renderable} [subtitle] - Information displayed right below the title when there is no highlited item.
 * This can for example be used to show a total.
 * @param {function} [getTitleWhenHighlighted] - Renderer taking the highlighted item as parameter and returning
 * what the doughnut title should be on highlight.
 * @param {function} [getSubtitleWhenHighlighted] - Renderer taking the highlighted item as parameter and returning
 * what the doughnut subtitle should be on highlight.
 * @param {func} [mouseOverCallback] - Callback to execute when the mouse hovers on and off an item segment.
 * Takes the item as parameter (or undefined when the mouse leaves the area).
 */
export default class DoughnutChartWidget extends Widget {
	constructor({
		items,
		radius = 120,
		doughnutWidth = 30,
		title = "",
		subtitle = "",
		getTitleWhenHighlighted = () => () => {},
		getSubtitleWhenHighlighted = () => () => {},
		mouseOverCallback = () => {},
	}) {
		super(...arguments);

		this._title = title;
		this._subtitle = subtitle;
		this._getTitleWhenHighlighted = getTitleWhenHighlighted;
		this._getSubtitleWhenHighlighted = getSubtitleWhenHighlighted;
		this._mouseOverCallback = mouseOverCallback;

		this._radius = radius;
		this._perimeter = this._radius * 2 * Math.PI;
		this._margin = this._radius / 3;
		this._height = this._radius * 2 + this._radius;
		this._width = this._height;
		this._doughnutWidth = doughnutWidth;
		this._viewboxValue = this._height / 3;

		const positiveItems = items.filter((x) => x.magnitude > 0);

		this._positiveTotal = positiveItems.reduce(
			(r, x) => x.magnitude + r,
			0,
		);

		this._initializeItems(positiveItems);

		// Defaults to none of the items to be highlighted.
		this._highlightedValue = undefined;
	}

	renderContentOn(html) {
		html.div(
			{ klass: "doughnut-chart", "data-test": "doughnut-chart" },
			html.svg(
				{
					height: `${this._height}`,
					width: `${this._width}`,
					viewbox: `0 0 ${this._viewboxValue} ${this._viewboxValue}`,
				},
				this._renderCircleSegmentsOn.bind(this),
				this._renderTotalOn.bind(this),
			),
		);
	}

	setHighlightedValue(value) {
		if (this._highlightedValue === value) {
			return;
		}

		this._highlightedValue = value;
		this.update();
	}

	_isHighlighted(value) {
		return value && this._highlightedValue === value;
	}

	_hasHighlightedItem() {
		return !!this._highlightedValue;
	}

	_renderTotalOn(html) {
		const interpolatedY = this._interpolateCoordinate(this._radius);

		// this rotation compensates the rotation of the svg
		const rotate = "rotate(90, 150, 150)";

		html.text(
			{
				x: "50%",
				y: `${interpolatedY}%`,
				fill: "grey",
				transform: rotate,
				"text-anchor": "middle",
				style: "font-size: 18px",
				"data-test": "doughnut-title",
			},
			this._titleRenderer(),
		);

		html.text(
			{
				x: "50%",
				y: `${interpolatedY + 10}%`,
				fill: "grey",
				transform: rotate,
				"text-anchor": "middle",
			},
			this._subtitleRenderer(),
		);
	}

	_titleRenderer() {
		return this._hasHighlightedItem()
			? this._getTitleWhenHighlighted(this._highlightedValue)
			: this._title;
	}

	_subtitleRenderer() {
		return this._hasHighlightedItem()
			? this._getSubtitleWhenHighlighted(this._highlightedValue)
			: this._subtitle;
	}

	_renderCircleSegmentsOn(html) {
		html.render(
			this._items.map((item) => this._circleSegmentRenderer(item)),
		);
	}

	_circleSegmentRenderer(item) {
		const dashoffset = -item.offset;

		return (html) => {
			html.circle({
				cx: `${this._height}` / 2,
				cy: `${this._height}` / 2,
				r: `${this._radius}`,
				"stroke-dasharray": `${item.length} ${
					this._perimeter - item.length
				}`,
				"stroke-dashoffset": dashoffset,
				"stroke-width": `${this._getItemWidth(item)}`,
				fill: "transparent",
				stroke: item.color,
				"pointer-events": "visibleStroke",
				mouseover: () => this._mouseOverCallback(item.value),
				mouseleave: () => this._mouseOverCallback(undefined),
				"data-test": "doughnut-segment",
			});
		};
	}

	_getItemWidth(item) {
		// make segment width 120% its regular size if item is highlighted.
		const widthMultiplier = this._isHighlighted(item.value) ? 1.2 : 1;

		return this._doughnutWidth * widthMultiplier;
	}

	_calculateLength(magnitude) {
		const ratio = magnitude / this._positiveTotal;

		return this._perimeter * ratio;
	}

	/**
	 * Estimate a good Y coordinate based on the doughnut radius.
	 */
	_interpolateCoordinate(radius) {
		// Find out a reasonable Y coordinate based on 2 well-known working points
		const minimum = { radius: 110, y: 35 };
		const maximum = { radius: 140, y: 20 };
		const interpolated =
			(radius - minimum.radius) / (maximum.radius - minimum.radius);
		return interpolated * (maximum.y - minimum.y) + minimum.y;
	}

	_initializeItems(items) {
		this._items = [];
		let offset = 0;

		for (const item of items) {
			const length = this._calculateLength(item.magnitude);
			this._items.push({ ...item, offset, length });
			offset += length;
		}
	}
}
